import * as React from "react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { Dialog as DialogPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"
import { Button, buttonVariants } from "@/components/ui/button"
import { useBackDismiss } from "@/lib/hooks/useBackDismiss"
import { InertAppRoot } from "@/lib/ui/inertAppRoot"
import { useControllableOpen } from "@/lib/ui/useControllableOpen"
import { layerOpenAutoFocus } from "@/lib/ui/layerFocus"
import {
  LAYER_CLOSE_X_PADDED_GAP4,
  LAYER_CLOSE_X_STICKY,
  LAYER_DESCRIPTION,
  LAYER_DIALOG,
  LAYER_HANDLE,
  LAYER_SCRIM,
  LAYER_SURFACE,
  LAYER_TITLE,
} from "@/lib/ui/layerShape"

/**
 * Story 24.3 — the shared Dialog (DESIGN.md › Components › Sheet and Dialog).
 * Roles only.
 *
 * - A bottom sheet below 1024px (grab handle, at most 90dvh, scrolls inside);
 *   centred at `lg`, at most 480px wide, `rounded-xl`. Raised surface,
 *   floating edge, `shadow-float-lg`, over the scrim.
 * - An always-present 44px ghost X: the content's first child, `sticky`
 *   top-right so it stays in view while the body scrolls
 *   (`showCloseButton={false}` hides it); `DialogHeader` leaves room for it.
 *   The content is a `flex flex-col gap-4` column (was a grid) so the X's
 *   negative margins take it out of the flow exactly.
 * - Opening focuses the first tabbable control (not the X), else the Title
 *   (`tabIndex={-1}`). `#root` is `inert` while the content is mounted.
 * - The root owns its open state and system Back closes it
 *   (`useBackDismiss`); `backDismiss={false}` opts out.
 */

type DialogProps = React.ComponentProps<typeof DialogPrimitive.Root> & {
  /** System Back closes the dialog (default `true`). */
  backDismiss?: boolean
}

function Dialog({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  backDismiss = true,
  ...props
}: DialogProps) {
  const [open, setOpen] = useControllableOpen(openProp, defaultOpen, onOpenChange)
  useBackDismiss(open, () => setOpen(false), backDismiss)
  return <DialogPrimitive.Root data-slot="dialog" open={open} onOpenChange={setOpen} {...props} />
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(LAYER_SCRIM, className)}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  onOpenAutoFocus,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        aria-modal="true"
        className={cn(LAYER_SURFACE, LAYER_DIALOG, "flex flex-col gap-4 p-6", className)}
        onOpenAutoFocus={layerOpenAutoFocus('[data-slot="dialog-title"]', onOpenAutoFocus)}
        {...props}
      >
        <InertAppRoot />
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close-x"
            aria-label="Close"
            className={cn(
              buttonVariants({ variant: "ghost", size: "icon" }),
              LAYER_CLOSE_X_STICKY,
              LAYER_CLOSE_X_PADDED_GAP4
            )}
          >
            <XMarkIcon aria-hidden="true" />
          </DialogPrimitive.Close>
        )}
        <div aria-hidden="true" data-slot="dialog-handle" className={cn(LAYER_HANDLE, "lg:hidden")} />
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      // Room on the right for the 44px X (8px in from the top and right
      // edges, so it reaches 28px into the p-6 content box).
      className={cn("flex flex-col gap-2 pr-8 text-left", className)}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="secondary">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      tabIndex={-1}
      className={cn(LAYER_TITLE, className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(LAYER_DESCRIPTION, className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
